import { Ref } from 'vue';
import { useRouter } from 'vue-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { AxiosError, AxiosResponse } from 'axios';

import { CommonResType } from '@/types/commonApiType';

import { api } from './_http';

type GetKnowhowParams = {
	search: string;
	page: number;
	row: number;
	sort: string;
};
type GetKnowhowData = {
	knowhow: KnowhowList[];
	knowhow_count: string;
};
type KnowhowList = {
	knowhow_best_yn: string;
	knowhow_enrollment_date: string;
	knowhow_fixed_yn: string;
	knowhow_hitcount: number;
	knowhow_likecount: number | null;
	knowhow_pk: number;
	knowhow_title: string;
	member_nickname: string;
	file_path: string;
	comment_count: number;
};
export const useGetKnowhowApi = (params: Ref<GetKnowhowParams>) => {
	const url = '/v1/front/getKnowhow';
	return useQuery<AxiosResponse, AxiosError, GetKnowhowData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data.data,
	});
};

type GetKnowhowDetailParams = {
	pk: number;
};

export type GetKnowhowDetailData = {
	knowhow: Knowhow;
	is_mine: boolean;
	my_like: boolean;
};
export type Knowhow = {
	knowhow_best_yn: string;
	file_path: null;
	file_pk: null;
	knowhow_category_pk: null;
	knowhow_content: string;
	knowhow_enrollment_date: string;
	knowhow_fixed_yn: string;
	knowhow_hitcount: number;
	knowhow_likecount: number | null;
	knowhow_modifying_date: null;
	knowhow_pk: number;
	knowhow_title: string;
	member_nickname: string;
	member_pk: number;
};
export const useGetKnowhowDetailApi = (params: GetKnowhowDetailParams) => {
	const url = '/v1/front/getKnowhowDetail';
	return useQuery<AxiosResponse, AxiosError, GetKnowhowDetailData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params),
		select: (res) => res.data?.data,
	});
};

export const useSaveKnowhowApi = () => {
	const url = '/v1/front/saveKnowhow';
	const queryClient = useQueryClient();
	return useMutation<AxiosResponse, AxiosError, FormData>({
		mutationFn: (data: FormData) => api.post(url, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['/v1/front/getKnowhow', '/v1/front/getKnowhowDetail'] });
		},
	});
};

type deleteKnowhowParams = {
	pk: number;
};
export const useDeleteKnowhowApi = () => {
	const url = '/v1/front/deleteKnowhow';
	const router = useRouter();
	return useMutation<AxiosResponse, AxiosError, deleteKnowhowParams>({
		mutationFn: (data: deleteKnowhowParams) => api.post(url, data),
		onSuccess: () => {
			router.push('/protips');
		},
	});
};

type AdjustLikesParams = {
	knowhow_pk: number;
};
export const useAdjustLikesApi = () => {
	const url = '/v1/front/adjustLikes';
	return useMutation<AxiosResponse, AxiosError, AdjustLikesParams>({
		mutationFn: (data: AdjustLikesParams) => api.post(url, data),
	});
};

type GetKnowhowAuthData = CommonResType;
// 노하우공유-게시글 작성 권한
export const useGetKnowhowAuthApi = () => {
	const url = '/v1/front/getKnowhowAuth';
	return useQuery<AxiosResponse, AxiosError, GetKnowhowAuthData>({
		queryKey: [url],
		queryFn: () => api.get(url),
		select: (res) => res.data,
		enabled: false,
	});
};

type GetKnowhowTabListData = {
	know_tabs: string[];
};
// 노하우공유-게시물 탭 리스트 조회
export const useGetKnowhowTabListApi = () => {
	const url = '/v1/front/getKnowhowTabList';
	return useQuery<AxiosResponse, AxiosError, GetKnowhowTabListData>({
		queryKey: [url],
		queryFn: () => api.get(url),
		select: (res) => res.data.data,
	});
};
